import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Etichetta, Riquadro, Tabella } from '../../componenti/base.tsx'
import {
  ChiudiRicontatto, EtichettaStato, ModuloRicontatto, quandoRicontatto,
  type RicontattiScheda, type Ricontatto,
} from '../../componenti/ricontatti.tsx'

/**
 * Ricontatti e appuntamenti della scheda (465, 560), in testa alla linguetta Percorsi: e' li' che si
 * registra quello che si fa per la persona, e un appuntamento preso per lei e' il passo successivo.
 * Prima quelli aperti, dal piu' vicino; quelli chiusi si aprono a richiesta. Un appuntamento fatto porta
 * a «Registra l'intervento», sul suo percorso.
 */
export function SezioneRicontatti({ personaId, dati, errore, ricarica, nuovo, chiudiNuovo, sedePersona = null }: {
  personaId: string
  dati: RicontattiScheda
  errore: string | null
  ricarica: () => void
  /** Aperto dai comandi «Fissa un ricontatto» e «Fissa un appuntamento» dell'intestazione della scheda. */
  nuovo: 'ricontatto' | 'appuntamento' | null
  chiudiNuovo: () => void
  sedePersona?: string | null
}) {
  const [modifica, setModifica] = useState<Ricontatto | 'ricontatto' | 'appuntamento' | null>(null)
  const [chiusura, setChiusura] = useState<Ricontatto | null>(null)
  const [esito, setEsito] = useState<ReactNode>(null)
  const [chiusi, setChiusi] = useState(false)

  const aperti = dati.ricontatti.filter((r) => r.stato === 'programmato' || r.stato === 'scaduto')
  const conclusi = dati.ricontatti.filter((r) => r.stato !== 'programmato' && r.stato !== 'scaduto')
  const registra = (r: Ricontatto) => `/persone/${personaId}/intervento${r.percorso_id ? `?percorso=${r.percorso_id}` : ''}`
  const fatto = (testo: string, stato?: string) => {
    const appuntamento = chiusura?.tipo === 'appuntamento' && stato === 'fatto' ? chiusura : null
    setModifica(null); setChiusura(null); chiudiNuovo(); ricarica()
    setEsito(appuntamento
      ? <>{testo} <Link to={registra(appuntamento)}>Registra l’intervento</Link></>
      : testo)
  }
  const inModifica = nuovo ?? modifica

  const righe = (elenco: Ricontatto[], comandi: boolean) => elenco.map((r) => (
    <tr key={r.id}>
      <td data-etichetta="Quando">
        {quandoRicontatto(r)}
        {r.tipo === 'appuntamento' && (
          <div className="piccolo tenue">
            <Etichetta tono="accento">appuntamento</Etichetta>{' '}
            {[r.durata_minuti ? `${r.durata_minuti} min` : null, r.sede_nome, r.risorsa_nome].filter(Boolean).join(' · ')}
          </div>
        )}
      </td>
      <td data-etichetta="Motivo" className="testo-libero">
        {r.motivo}
        {r.esito && <div className="piccolo tenue">{r.esito}</div>}
      </td>
      <td data-etichetta="Chi lo segue">{r.responsabile ?? '—'}</td>
      <td data-etichetta="Percorso" className="piccolo">{r.percorso ?? '—'}</td>
      <td data-etichetta="Stato"><EtichettaStato r={r} /></td>
      <td data-etichetta="">
        {!comandi && r.tipo === 'appuntamento' && r.stato === 'fatto' && (
          <Link className="bottone bottone-discreto bottone-piccolo" to={registra(r)}>Registra l’intervento</Link>
        )}
        {comandi && (
          <div className="riga riga-a-destra">
            <Bottone variante="secondario" dimensione="piccolo" onClick={() => { setEsito(null); setChiusura(r) }}>
              Com’è andato?
            </Bottone>
            <Bottone variante="discreto" dimensione="piccolo" onClick={() => { setEsito(null); setModifica(r) }}>
              Modifica
            </Bottone>
          </div>
        )}
      </td>
    </tr>
  ))

  return (
    <>
      <Riquadro denso titolo="Ricontatti e appuntamenti"
                azioni={<>
                  <Bottone variante="secondario" dimensione="piccolo"
                           onClick={() => { setEsito(null); setModifica('appuntamento') }}>
                    Fissa un appuntamento
                  </Bottone>
                  <Bottone variante="secondario" dimensione="piccolo"
                           onClick={() => { setEsito(null); setModifica('ricontatto') }}>
                    Fissa un ricontatto
                  </Bottone>
                </>}>
        {esito && <Avviso tipo="successo">{esito}</Avviso>}
        {errore && <Avviso tipo="errore">{errore}</Avviso>}
        {aperti.length === 0
          ? <p className="tenue piccolo">Nessun ricontatto in programma.</p>
          : (
            <Tabella intestazioni={['Quando', 'Motivo', 'Chi lo segue', 'Percorso', 'Stato', '']}>
              {righe(aperti, true)}
            </Tabella>
          )}
        {conclusi.length > 0 && (
          <div className="riga">
            <Bottone variante="discreto" dimensione="piccolo" onClick={() => setChiusi((v) => !v)}>
              {chiusi ? 'Nascondi quelli chiusi' : `Mostra quelli chiusi (${conclusi.length})`}
            </Bottone>
          </div>
        )}
        {chiusi && conclusi.length > 0 && (
          <Tabella intestazioni={['Quando', 'Motivo ed esito', 'Chi lo seguiva', 'Percorso', 'Stato', '']}>
            {righe(conclusi, false)}
          </Tabella>
        )}
      </Riquadro>

      {inModifica && (
        <ModuloRicontatto personaId={personaId} dati={dati} sedePersona={sedePersona}
                          ricontatto={typeof inModifica === 'string' ? null : inModifica}
                          appuntamento={inModifica === 'appuntamento'}
                          chiudi={() => { setModifica(null); chiudiNuovo() }} fatto={fatto} />
      )}
      {chiusura && <ChiudiRicontatto ricontatto={chiusura} chiudi={() => setChiusura(null)} fatto={fatto} />}
    </>
  )
}
